import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useAuth from '../hooks/global/useAuth';
import useAppState from '../hooks/global/useAppState';
import useRouter from '../hooks/navigation/useRouter';
import useAppMethods from '../hooks/utility/useAppMethods';
import OtpFlow, { OtpFlowHandle } from '../components-library/auth/OtpFlow';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import { Drawer } from '@mui/material';
import { Link } from 'react-router-dom';
import { SCREENS } from '../services/exports/Constants';
import useOrderFlow from '../hooks/order/useOrderFlow';
import { EmailField, FirstNameField, LastNameField } from '../components-library/checkout/sections/CustomerInfoSection';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useData from '../hooks/utility/useData';
import ProfileManager from '../services/api/ProfileManager';
import moment from 'moment';
import MarketingSubscriptionConsent from '../components-library/marketing/MarketingSubscriptionConsent';
import useTheme from '../hooks/ui/useTheme';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import { delay } from '../utils/loading';
import { getCuisineEmoji } from '../utils/cuisineEmoji';
import { ArrowLeft, ArrowRight, Check, Loader2, Star, X } from 'lucide-react';
import classnames from 'classnames';
import { mailableLanguage } from '../services/localization/Languages';

enum Steps {
  Phone = 'phone',
  Otp = 'otp',
  Profile = 'profile',
}

const STEP_ORDER = [Steps.Phone, Steps.Otp, Steps.Profile];

const LoginModal = () => {
  const { t, i18n } = useTranslation(null, { keyPrefix: 'Modals:LoginModal' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { openModal, title, isStatic, initialPhoneNumber, onCloseRedirect } = useAppState().loginModalProps;

  const { toggleLoginModal } = useAppMethods();
  const { navigate, generatePath } = useRouter();
  const { isLoggedIn } = useAuth();
  const { localData } = useOrderFlow();
  const theme = useTheme();
  const { cashbackProgram, hasCashbackProgram, formatPointsAsMoney } = useCashbackProgram();

  const [step, setStep] = useState(Steps.Phone);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [otpRequestedFor, setOtpRequestedFor] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (step === Steps.Otp && otpRequestedFor && isComplete) {
      _onSubmit();
    }
  }, [isComplete]);

  const initialData = {
    first_name: localData.customer_first_name,
    last_name: localData.customer_last_name,
    email: localData.customer_email,
    birthday: null,
    is_marketing_subscriber: false,
    lang: mailableLanguage(i18n.language),
  };

  const { data, setData, update } = useData(initialData);

  const otpFlowRef = useRef<OtpFlowHandle>(null);

  const _onSubmit = async () => {
    setLoading(true);
    const handler = {
      [Steps.Phone]: async () => await onRequestOtp(),
      [Steps.Otp]: async () => await onLogIn(),
      [Steps.Profile]: async () => await onUpdateProfile(),
    }[step];

    await delay();
    await handler();
    setLoading(false);
  };

  const onRequestOtp = async () => {
    if (!otpFlowRef.current) {
      return;
    }

    await otpFlowRef.current.requestCode();
  };

  const onLogIn = async () => {
    if (!otpFlowRef.current) {
      return;
    }

    const { success, response } = await otpFlowRef.current.logIn({
      company_id: company?.id,
    });

    if (!success) {
      return toast.error(t('toasts.invalid_code'));
    }

    if (
      Boolean(response.data.user.email) &&
      Boolean(response.data.user.first_name) &&
      Boolean(response.data.user.last_name) &&
      Boolean(response.data.user.is_marketing_subscriber)
    ) {
      return closeModal(true);
    }

    setData((current) => ({
      ...current,
      first_name: localData.customer_first_name ?? response.data.user.first_name,
      last_name: localData.customer_last_name ?? response.data.user.last_name,
      email: localData.customer_email ?? response.data.user.email,
      birthday: response.data.user.birthday ? moment(response.data.user.birthday).utc(true) : null,
      is_marketing_subscriber: Boolean(response.data.user.is_marketing_subscriber),
    }));

    return setStep(Steps.Profile);
  };

  const onUpdateProfile = async () => {
    const { success } = await ProfileManager.putProfile(company?.id, data);

    if (!success) {
      return toast.error(t('toasts.failed_to_update_profile'));
    }

    return closeModal(true);
  };

  function resetState() {
    setStep(Steps.Phone);
    setLoading(false);
    setPhoneNumber(null);
    setOtpRequestedFor(null);
    setIsComplete(false);
    setData(initialData);
  }

  function closeModal(success?: boolean): void {
    toggleLoginModal({ openModal: false });
    resetState();

    if (onCloseRedirect) {
      return onCloseRedirect(isLoggedIn || success);
    }

    if (!isLoggedIn) {
      navigate({
        web: {
          to: -1,
        },
      });
    }
  }

  const currentStepIndex = STEP_ORDER.indexOf(step);

  const cuisineEmoji = getCuisineEmoji(company?.cuisine_types);

  const howItWorksSteps = [
    { emoji: '📲', title: t('how_it_works.step_1.title'), desc: t('how_it_works.step_1.desc') },
    {
      emoji: cuisineEmoji,
      title: t('how_it_works.step_2.title'),
      desc: t('how_it_works.step_2.desc', { percentage: cashbackProgram?.percentage ?? 0 }),
    },
    {
      emoji: '🎁',
      title: t('how_it_works.step_3.title'),
      desc: t('how_it_works.step_3.desc', {
        amount: cashbackProgram?.max_spending_limit ? formatPointsAsMoney(cashbackProgram.max_spending_limit) : '',
      }),
    },
  ];

  const backdrop = theme.isDarkMode
    ? {
        backgroundColor: 'rgba(52, 52, 52, 0.5)',
        backdropFilter: 'blur(4px)',
      }
    : {};

  return (
    <Drawer
      open={openModal}
      anchor="right"
      onClose={() => closeModal()}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100vw', sm: '448px' },
          },
        },
        backdrop: {
          sx: backdrop,
        },
      }}
      ModalProps={{
        disableEnforceFocus: true,
      }}
    >
      <div className="h-full flex flex-col bg-brand-background-primary">
        {/* Sticky header */}
        <div className={stickyHeader}>
          <div className="px-md py-sm flex items-center justify-between">
            <div className="flex items-center gap-x-xs">
              {step !== Steps.Phone && (
                <button
                  onClick={() => setStep(step === Steps.Profile ? Steps.Otp : Steps.Phone)}
                  className={backButton}
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-x-xs">
                <div className={starBadge}>
                  <Star className="w-3.5 h-3.5 text-brand-primary fill-brand-primary" />
                </div>
                <h2 className="text-sm font-bold text-brand-text-primary">
                  {title ?? t('title', { name: cashbackProgram?.title ?? t('default_program_name') })}
                </h2>
              </div>
            </div>
            <button onClick={() => closeModal()} className={closeButton}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="px-md pb-sm flex gap-1.5">
            {STEP_ORDER.map((s, i) => (
              <div
                key={s}
                className={classnames('h-1 rounded-full flex-1 transition-colors duration-300', {
                  'bg-brand-primary': i <= currentStepIndex,
                  'bg-gray-200 dark:bg-gray-700': i > currentStepIndex,
                })}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-md py-md">
          {/* OtpFlow is always mounted so it preserves state across Phone/Otp steps */}
          <div className={step === Steps.Phone || step === Steps.Otp ? '' : 'hidden'}>
            {/* Phone step: show promo content above OtpFlow */}
            {step === Steps.Phone && hasCashbackProgram && (
              <div className="space-y-md mb-md">
                {/* Savings highlight */}
                <div className={savingsHighlight}>
                  <p className="text-lg font-bold text-brand-primary mb-2xs">{t('savings_highlight.title')}</p>
                  <p className="text-xs text-muted-foreground">{t('savings_highlight.subtitle')}</p>
                </div>

                {/* How it works */}
                <div>
                  <p className={howItWorksLabel}>{t('how_it_works.label')}</p>
                  <div className="flex gap-xs">
                    {howItWorksSteps.map((s, i) => (
                      <div key={i} className={howItWorksCard}>
                        <div className={howItWorksNumber}>{i + 1}</div>
                        {cuisineEmoji && <span className="text-lg block mb-2xs mt-2xs">{s.emoji}</span>}
                        <p className="text-2xs font-semibold text-brand-text-primary mb-2xs">{s.title}</p>
                        <p className="text-2xs text-muted-foreground leading-snug">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* OTP Flow — always rendered, handles phone + OTP internally */}
            <OtpFlow
              ref={otpFlowRef}
              isStatic={isStatic || step === Steps.Otp}
              hideOtp={step === Steps.Phone}
              initialPhoneNumber={initialPhoneNumber}
              onPhoneNumberChange={setPhoneNumber}
              onValidationStatusChange={(result: boolean) => setIsComplete(result)}
              onCodeRequested={(phone: string) => {
                setOtpRequestedFor(phone);
                setStep(Steps.Otp);
              }}
              phoneNumberLabel={
                <>
                  <h6 className="mb-2xs font-semibold text-sm">{t('form.phone_number.label')}</h6>
                  <p className="text-xs text-muted-foreground">{t('form.phone_number.description')}</p>
                </>
              }
            />

            {/* Continue / Verify button */}
            <button
              onClick={_onSubmit}
              disabled={!isComplete || loading}
              className={classnames(primaryButton, 'mt-md')}
              data-test="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {step === Steps.Otp ? t('buttons.verify') : t('buttons.continue')} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Terms */}
            <p className="text-2xs text-muted-foreground text-center leading-relaxed mt-md">
              <Trans
                t={t}
                components={{
                  TermsLink: (
                    <Link
                      to={generatePath(SCREENS.TERMS_AND_CONDITIONS)}
                      target="_blank"
                      className="underline text-brand-primary"
                    />
                  ),
                  PrivacyPolicyLink: (
                    <Link
                      to={generatePath(SCREENS.PRIVACY_POLICY)}
                      target="_blank"
                      className="underline text-brand-primary"
                    />
                  ),
                }}
              >
                terms
              </Trans>
            </p>
          </div>

          {step === Steps.Profile && (
            <div className="space-y-sm">
              {/* Verified badge */}
              <div className="text-center mb-sm">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-sm">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-md font-bold text-brand-text-primary mb-2xs">{t('profile.verified_title')}</h3>
                <p className="text-sm text-muted-foreground">{t('profile.verified_subtitle')}</p>
              </div>

              {/* Verified phone display */}
              <div className={verifiedPhoneBadge}>
                <Check className="w-4 h-4 text-green-600 shrink-0" />
                <span className="text-sm text-brand-text-primary">{phoneNumber}</span>
              </div>

              {/* Form fields */}
              <EmailField value={data.email} onChange={(value: string) => update('email', value)} />
              <div className="grid grid-cols-2 gap-xs">
                <FirstNameField value={data.first_name} onChange={(value: string) => update('first_name', value)} />
                <LastNameField value={data.last_name} onChange={(value: string) => update('last_name', value)} />
              </div>
              <div>
                <label htmlFor="birthday" className="text-sm font-semibold">
                  {t('form.birthday.label')}
                </label>
                <div className="mt-xs">
                  <DatePicker
                    value={data.birthday}
                    format="DD/MM"
                    localeText={{
                      fieldDayPlaceholder: () => t('form.birthday.parts.day'),
                      fieldMonthPlaceholder: () => t('form.birthday.parts.month'),
                    }}
                    onChange={(value) => update('birthday', value ? value.utc(true) : null)}
                    openTo="month"
                    views={['month', 'day']}
                    className="w-full"
                    slotProps={{
                      // Match the other inputs (CheckmarkTextInput): 36px tall, p-sm (12px)
                      // padding, text-sm. The "Birthday" caption above already labels the field,
                      // so the floating MUI label is dropped to keep the height compact. Padding
                      // goes on the input (not the root) so it doesn't stack with the input's own.
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            minHeight: 36,
                            height: 36,
                            borderRadius: 'var(--border-radius-md)',
                            fontSize: '0.875rem',
                          },
                          '& .MuiOutlinedInput-input': {
                            paddingTop: 0,
                            paddingBottom: 0,
                            paddingLeft: '12px',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <MarketingSubscriptionConsent
                checked={data.is_marketing_subscriber}
                onChange={(checked) => update('is_marketing_subscriber', checked)}
              />

              {/* Complete button */}
              <button onClick={_onSubmit} disabled={loading} className={primaryButton}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {t('buttons.complete')} <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Terms */}
              <p className="text-2xs text-muted-foreground text-center leading-relaxed">
                <Trans
                  t={t}
                  components={{
                    TermsLink: (
                      <Link
                        to={generatePath(SCREENS.TERMS_AND_CONDITIONS)}
                        target="_blank"
                        className="underline text-brand-primary"
                      />
                    ),
                    PrivacyPolicyLink: (
                      <Link
                        to={generatePath(SCREENS.PRIVACY_POLICY)}
                        target="_blank"
                        className="underline text-brand-primary"
                      />
                    ),
                  }}
                >
                  terms
                </Trans>
              </p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default LoginModal;

// Styles
const stickyHeader = 'sticky top-0 bg-brand-background-primary/95 backdrop-blur-sm z-10 border-b border-brand-border';
const backButton = 'text-muted-foreground hover:text-brand-text-primary transition-colors';
const starBadge = 'w-7 h-7 rounded-full bg-brand-primary/15 flex items-center justify-center';
const closeButton =
  'text-muted-foreground hover:text-brand-text-primary transition-colors p-2xs rounded-lg hover:bg-brand-background-hover';
const savingsHighlight = 'bg-brand-primary/10 border border-brand-primary/15 rounded-xl p-sm text-center';
const howItWorksLabel = 'text-2xs uppercase tracking-widest text-muted-foreground font-bold mb-sm';
const howItWorksCard = 'flex-1 bg-brand-card border border-brand-border rounded-xl p-sm text-center relative';
const howItWorksNumber =
  'absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-brand-primary text-brand-button-primary-text flex items-center justify-center text-2xs font-bold';
const primaryButton =
  'w-full bg-brand-button-primary-background text-brand-button-primary-text font-bold py-sm rounded-full text-sm hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-xs';
const verifiedPhoneBadge =
  'flex items-center gap-xs bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-sm py-xs';
